import React, { useState, useEffect } from 'react';
import { 
  CalendarIcon, 
  PlusIcon, 
  BeakerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { vaccinationApi, VaccinationSchedule, VaccinationSummary } from '../../api/vaccinations';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface VaccinationManagementProps {
  onAddVaccination: () => void;
  onRefresh: () => void;
}

const VaccinationManagement: React.FC<VaccinationManagementProps> = ({
  onAddVaccination,
  onRefresh,
}) => {
  const [vaccinations, setVaccinations] = useState<VaccinationSchedule[]>([]);
  const [summary, setSummary] = useState<VaccinationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('upcoming');
  const [showAdministerModal, setShowAdministerModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<VaccinationSchedule | null>(null);
  const [editCost, setEditCost] = useState<number>(0);

  useEffect(() => {
    loadVaccinationData();
  }, []);

  const loadVaccinationData = async () => {
    try {
      setLoading(true);
      const [vaccinationsData, summaryData] = await Promise.all([
        vaccinationApi.getAll(),
        vaccinationApi.getSummary(),
      ]);
      setVaccinations(vaccinationsData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading vaccination data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SCHEDULED': return 'text-cyan-600 bg-blue-100';
      case 'ADMINISTERED': return 'text-green-600 bg-green-100';
      case 'OVERDUE': return 'text-red-600 bg-red-100';
      case 'CANCELLED': return 'text-slate-600 bg-gray-100';
      case 'POSTPONED': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-slate-600 bg-gray-100';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ROUTINE': return 'text-cyan-600 bg-blue-100 border-blue-200';
      case 'EMERGENCY': return 'text-red-600 bg-red-100 border-red-200';
      case 'BOOSTER': return 'text-green-600 bg-green-100 border-green-200';
      case 'SEASONAL': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'PREVENTIVE': return 'text-purple-600 bg-purple-100 border-purple-200';
      default: return 'text-slate-600 bg-gray-100 border-gray-200';
    }
  };

  const handleAdministerVaccination = async (vaccinationId: string, data: any) => {
    try {
      await vaccinationApi.administer(vaccinationId, data);
      setShowAdministerModal(null);
      await loadVaccinationData();
      onRefresh();
    } catch (error) {
      console.error('Error administering vaccination:', error);
      alert('Failed to administer vaccination');
    }
  };

  const handleEditVaccination = (vaccination: VaccinationSchedule) => {
    setShowEditModal(vaccination);
    setEditCost(vaccination.cost || 0);
  };

  const handleUpdateVaccination = async (vaccinationId: string, data: any) => {
    try {
      await vaccinationApi.update(vaccinationId, data);
      setShowEditModal(null);
      await loadVaccinationData();
      onRefresh();
    } catch (error) {
      console.error('Error updating vaccination:', error);
      alert('Failed to update vaccination');
    }
  };

  const handleDeleteVaccination = async (vaccination: VaccinationSchedule) => {
    if (window.confirm(`Are you sure you want to delete the vaccination "${vaccination.vaccineName}" for bird ${vaccination.bird?.bandId}?`)) {
      try {
        await vaccinationApi.delete(vaccination.id);
        await loadVaccinationData();
        onRefresh();
      } catch (error) {
        console.error('Error deleting vaccination:', error);
        alert('Failed to delete vaccination');
      }
    }
  };

  const handleCancelVaccination = async (vaccination: VaccinationSchedule) => {
    const reason = prompt('Please provide a reason for cancellation:');
    if (reason !== null) {
      try {
        await vaccinationApi.cancel(vaccination.id, reason);
        await loadVaccinationData();
        onRefresh();
      } catch (error) {
        console.error('Error cancelling vaccination:', error);
        alert('Failed to cancel vaccination');
      }
    }
  };

  const handlePostponeVaccination = async (vaccination: VaccinationSchedule) => {
    const newDate = prompt('Enter new date (YYYY-MM-DD HH:MM):');
    const reason = prompt('Please provide a reason for postponement:');
    if (newDate && reason) {
      try {
        await vaccinationApi.postpone(vaccination.id, newDate, reason);
        await loadVaccinationData();
        onRefresh();
      } catch (error) {
        console.error('Error postponing vaccination:', error);
        alert('Failed to postpone vaccination');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Scheduled</dt>
                  <dd className="text-lg font-medium text-slate-900">{summary?.totalScheduled || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Administered</dt>
                  <dd className="text-lg font-medium text-slate-900">{summary?.totalAdministered || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Due Soon</dt>
                  <dd className="text-lg font-medium text-slate-900">{summary?.upcomingVaccinations?.length || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Overdue</dt>
                  <dd className="text-lg font-medium text-slate-900">{summary?.totalOverdue || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-3">
          <button
            onClick={() => setSelectedTab('upcoming')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              selectedTab === 'upcoming'
                ? 'bg-cyan-100 text-cyan-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setSelectedTab('overdue')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              selectedTab === 'overdue'
                ? 'bg-red-100 text-red-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Overdue
          </button>
          <button
            onClick={() => setSelectedTab('recent')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              selectedTab === 'recent'
                ? 'bg-green-100 text-green-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Recent
          </button>
          <button
            onClick={() => setSelectedTab('all')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              selectedTab === 'all'
                ? 'bg-gray-100 text-slate-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            All
          </button>
        </div>

        <button
          onClick={onAddVaccination}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Schedule Vaccination
        </button>
      </div>

      {/* Vaccinations List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {(selectedTab === 'upcoming' ? summary?.upcomingVaccinations :
            selectedTab === 'overdue' ? summary?.overdueVaccinations :
            selectedTab === 'recent' ? summary?.recentVaccinations :
            vaccinations)?.map((vaccination) => (
            <li key={vaccination.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <BeakerIcon className="h-6 w-6 text-slate-400" />
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-cyan-600 truncate">
                          {vaccination.vaccineName}
                        </p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(vaccination.vaccineType)}`}>
                          {vaccination.vaccineType}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-slate-500">
                        <p>
                          Bird: {vaccination.bird?.bandId} ({vaccination.bird?.species?.name})
                        </p>
                        <span className="mx-2">•</span>
                        <p>
                          {vaccination.vaccinationStatus === 'ADMINISTERED' ? 'Administered' : 'Scheduled'}: {' '}
                          {new Date(vaccination.vaccinationStatus === 'ADMINISTERED' && vaccination.administeredDate ? 
                            vaccination.administeredDate : vaccination.scheduledDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vaccination.vaccinationStatus)}`}>
                      {vaccination.vaccinationStatus}
                    </span>
                    <div className="flex space-x-1">
                      {vaccination.vaccinationStatus === 'SCHEDULED' && (
                        <>
                          <button
                            onClick={() => setShowAdministerModal(vaccination.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                          >
                            Administer
                          </button>
                          <button
                            onClick={() => handlePostponeVaccination(vaccination)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200"
                          >
                            Postpone
                          </button>
                          <button
                            onClick={() => handleCancelVaccination(vaccination)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleEditVaccination(vaccination)}
                        className="text-cyan-600 hover:text-indigo-900 p-1"
                        title="Edit vaccination"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteVaccination(vaccination)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete vaccination"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                {vaccination.veterinarian && (
                  <div className="mt-2 text-sm text-slate-500">
                    Veterinarian: {vaccination.veterinarian}
                    {vaccination.clinicName && ` at ${vaccination.clinicName}`}
                  </div>
                )}
                {vaccination.sideEffects && (
                  <div className="mt-2 text-sm text-red-600">
                    Side effects: {vaccination.sideEffects}
                  </div>
                )}
              </div>
            </li>
          )) || (
            <li>
              <div className="px-4 py-12 text-center">
                <BeakerIcon className="mx-auto h-12 w-12 text-slate-400" />
                <h3 className="mt-2 text-sm font-medium text-slate-900">No vaccinations</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedTab === 'upcoming' ? 'No upcoming vaccinations scheduled.' :
                   selectedTab === 'overdue' ? 'No overdue vaccinations.' :
                   selectedTab === 'recent' ? 'No recent vaccinations.' :
                   'No vaccinations found.'}
                </p>
              </div>
            </li>
          )}
        </ul>
      </div>

      {/* Administer Vaccination Modal */}
      <BrandModal
        open={!!showAdministerModal}
        onClose={() => setShowAdministerModal(null)}
        maxWidthClass="max-w-md"
      >
        {showAdministerModal ? (
          <>
            <BrandModalHeader
              title="Administer Vaccination"
              onClose={() => setShowAdministerModal(null)}
            />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleAdministerVaccination(showAdministerModal, {
                  administeredDate: formData.get('administeredDate'),
                  veterinarian: formData.get('veterinarian'),
                  clinicName: formData.get('clinicName'),
                  batchNumber: formData.get('batchNumber'),
                  manufacturer: formData.get('manufacturer'),
                  sideEffects: formData.get('sideEffects'),
                  notes: formData.get('notes'),
                });
              }}
            >
              <BrandModalBody>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Administration Date *
                    </label>
                    <input
                      type="datetime-local"
                      name="administeredDate"
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      defaultValue={new Date().toISOString().slice(0, 16)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Veterinarian
                    </label>
                    <input
                      type="text"
                      name="veterinarian"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Clinic Name
                    </label>
                    <input
                      type="text"
                      name="clinicName"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Batch Number
                    </label>
                    <input
                      type="text"
                      name="batchNumber"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Manufacturer
                    </label>
                    <input
                      type="text"
                      name="manufacturer"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Side Effects
                    </label>
                    <textarea
                      name="sideEffects"
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>
                </div>
              </BrandModalBody>
              <BrandModalFooter>
                <button
                  type="button"
                  onClick={() => setShowAdministerModal(null)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Administer
                </button>
              </BrandModalFooter>
            </form>
          </>
        ) : null}
      </BrandModal>

      {/* Edit Vaccination Modal */}
      <BrandModal
        open={!!showEditModal}
        onClose={() => setShowEditModal(null)}
        maxWidthClass="max-w-2xl"
      >
        {showEditModal ? (
          <>
            <BrandModalHeader title="Edit Vaccination" onClose={() => setShowEditModal(null)} />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleUpdateVaccination(showEditModal.id, {
                  vaccineName: formData.get('vaccineName'),
                  vaccineType: formData.get('vaccineType'),
                  scheduledDate: formData.get('scheduledDate'),
                  veterinarian: formData.get('veterinarian'),
                  clinicName: formData.get('clinicName'),
                  notes: formData.get('notes'),
                  cost: editCost,
                });
              }}
            >
              <BrandModalBody>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Vaccine Name *
                    </label>
                    <input
                      type="text"
                      name="vaccineName"
                      required
                      defaultValue={showEditModal.vaccineName}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Vaccine Type
                    </label>
                    <select
                      name="vaccineType"
                      defaultValue={showEditModal.vaccineType}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="ROUTINE-1753461297326" value="ROUTINE">Routine</option>
                      <option key="EMERGENCY-1753461297326" value="EMERGENCY">Emergency</option>
                      <option key="BOOSTER-1753461297326" value="BOOSTER">Booster</option>
                      <option key="SEASONAL-1753461297326" value="SEASONAL">Seasonal</option>
                      <option key="PREVENTIVE-1753461297326" value="PREVENTIVE">Preventive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Scheduled Date
                    </label>
                    <input
                      type="datetime-local"
                      name="scheduledDate"
                      defaultValue={new Date(showEditModal.scheduledDate).toISOString().slice(0, 16)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Cost ($)
                    </label>
                    <AmountInput
                      value={editCost}
                      onChange={(v) => setEditCost(v ?? 0)}
                      min={0}
                      inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Veterinarian
                    </label>
                    <input
                      type="text"
                      name="veterinarian"
                      defaultValue={showEditModal.veterinarian || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Clinic Name
                    </label>
                    <input
                      type="text"
                      name="clinicName"
                      defaultValue={showEditModal.clinicName || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      defaultValue={showEditModal.notes || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>
                </div>
              </BrandModalBody>
              <BrandModalFooter>
                <button
                  type="button"
                  onClick={() => setShowEditModal(null)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Vaccination
                </button>
              </BrandModalFooter>
            </form>
          </>
        ) : null}
      </BrandModal>
    </div>
  );
};

export default VaccinationManagement; 